import { Calendar, MapPin, Save, CheckCircle, ArrowLeft, FileText, Check, MoreVertical, Copy, Pencil, Hash } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SaveStatusIndicator } from "../../../components/SaveStatusIndicator";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import type { SaveStatus } from "../../../hooks/useAutoSave";
import type { Meeting } from "../../../services/meeting.service";
import type { Project } from "../../../services/project.service";
import { CollaboratorIndicator } from "../../../components/CollaboratorIndicator";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { LIMITS } from "../../../lib/constants";
import {
  MeetingMetadataFields,
  type ResponsibleOption,
} from "./MeetingMetadataFields";

interface MeetingHeaderProps {
  meeting: Meeting;
  project: Project;
  projectId: string;
  saveStatus?: SaveStatus;
  meetingName: string;
  onNameChange?: (name: string) => void;
  legenda: string;
  observacoes: string;
  responsavelId: number | null;
  responsibleOptions: readonly ResponsibleOption[];
  onLegendaChange: (value: string) => void;
  onObservacoesChange: (value: string) => void;
  onResponsavelChange: (value: number | null) => void;
  onSave?: () => void;
  onFinalize?: () => void;
  onReopen?: () => void;
  onBack?: () => void;
  onExportPDF?: () => void;
  onClone?: () => void;
  onEditProjectCode?: () => void;
  currentDate?: string;
  currentHoraInicio?: string | null;
  currentHoraFim?: string | null;
  canEdit?: boolean;
}

export function MeetingHeader({
  meeting,
  project,
  projectId,
  saveStatus,
  meetingName,
  onNameChange,
  legenda,
  observacoes,
  responsavelId,
  responsibleOptions,
  onLegendaChange,
  onObservacoesChange,
  onResponsavelChange,
  onSave,
  onFinalize,
  onReopen,
  onBack,
  onExportPDF,
  onClone,
  onEditProjectCode,
  currentDate,
  currentHoraInicio,
  currentHoraFim,
  canEdit = true,
}: MeetingHeaderProps) {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleStartEditName = () => {
    if (meeting.status === "final" || !canEdit) return;
    setEditingNameValue(meetingName || t('meetings.meeting_number', { number: meeting.number }));
    setIsEditingName(true);
  };

  const handleSaveName = () => {
    const trimmed = editingNameValue.trim();
    if (trimmed && onNameChange) {
      onNameChange(trimmed);
    }
    setIsEditingName(false);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditingNameValue("");
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelEditName();
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(`/projects/${projectId}/meetings`);
    }
  };

  const isSaveButtonDisabled = saveStatus === 'idle' || saveStatus === 'saved';
  const isSaving = saveStatus === 'saving';

  const isSaved = saveStatus === 'idle' || saveStatus === 'saved';

  const getSaveButtonText = () => {
    if (isSaving) return t('saving');
    if (saveStatus === 'idle' || saveStatus === 'saved') return t('saveStatus.saved');
    return t('save');
  };

  const handleSaveClick = () => {
    if (onSave && !isSaveButtonDisabled) {
      onSave();
    }
  };

  const handleMouseEnter = () => {
    if (isSaveButtonDisabled && !isSaving) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {/* Botão voltar */}
          <button
            onClick={handleBack}
            className="p-2 hover:bg-muted rounded-md transition-colors flex-shrink-0"
            aria-label={t('meetingHeader.back_to_meetings')}
          >
            <ArrowLeft size={18} className="text-muted-foreground" />
          </button>

          {/* Info principal */}
          <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {isEditingName ? (
                <Input
                  ref={nameInputRef}
                  type="text"
                  value={editingNameValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.length <= LIMITS.MEETING_NAME_MAX) {
                      setEditingNameValue(val);
                    } else {
                      setEditingNameValue(val.slice(0, LIMITS.MEETING_NAME_MAX));
                    }
                  }}
                  maxLength={LIMITS.MEETING_NAME_MAX}
                  onBlur={handleSaveName}
                  onKeyDown={handleNameKeyDown}
                  className="h-8 w-64 max-w-full text-lg font-semibold"
                />
              ) : (
                <button
                  onClick={handleStartEditName}
                  className={`group flex items-center gap-1.5 text-lg font-semibold text-foreground truncate hover:text-foreground/80 transition-colors ${
                    meeting.status === "final" ? "cursor-default" : "cursor-pointer"
                  }`}
                  title={meeting.status !== "final" ? t('edit') : undefined}
                >
                  <span className="truncate">{meetingName || t('meetings.meeting_number', { number: meeting.number })}</span>
                  {meeting.status !== "final" && (
                    <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  )}
                </button>
              )}
              <span className="text-muted-foreground hidden sm:inline">•</span>
              <span className="text-sm text-muted-foreground truncate hidden sm:inline">{project.name}</span>
            </div>

            {/* Metadata inline */}
            <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                <span>{formatDate(currentDate || meeting.date)}</span>
                {(currentHoraInicio || meeting.hora_inicio) && (
                  <span className="ml-1">
                    {currentHoraInicio || meeting.hora_inicio}
                    {(currentHoraFim || meeting.hora_fim) && (
                      <span> – {currentHoraFim || meeting.hora_fim}</span>
                    )}
                  </span>
                )}
              </div>
              {meeting.location && (
                <>
                  <span className="text-muted-foreground/40">•</span>
                  <div className="flex items-center gap-1 truncate max-w-32">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{meeting.location}</span>
                  </div>
                </>
              )}
              <Badge
                variant="secondary"
                className={`text-xs px-1.5 py-0 h-5 ${
                  meeting.status === "final"
                    ? "bg-green-100 text-green-700 hover:bg-green-100"
                    : "bg-yellow-100 text-yellow-700 hover:bg-yellow-100"
                }`}
              >
                {meeting.status === "final" ? t('meetings.status_final') : t('meetings.status_draft')}
              </Badge>
              <CollaboratorIndicator
                resourceType="minute"
                resourceId={Number(meeting.id)}
              />
            </div>
          </div>

          {/* Ações */}
          <div className="ml-auto flex w-full max-w-full flex-shrink-0 flex-wrap items-center justify-end gap-2 sm:w-auto">
            {/* Save status indicator */}
            {saveStatus && saveStatus !== 'idle' && saveStatus !== 'saved' && canEdit && (
              <SaveStatusIndicator status={saveStatus} />
            )}

            {/* Botão Salvar */}
            {onSave && canEdit && (
              <div
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <Button
                  onClick={handleSaveClick}
                  disabled={isSaveButtonDisabled || isSaving}
                  variant={isSaved ? "outline" : "default"}
                  size="sm"
                  className={`${isSaveButtonDisabled ? 'opacity-60' : ''}`}
                  aria-label={isSaveButtonDisabled ? t('saveStatus.saved') : t('save')}
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current mr-1.5"></div>
                      <span className="hidden sm:inline">{getSaveButtonText()}</span>
                    </>
                  ) : (
                    <>
                      {isSaved ? (
                        <Check className="h-4 w-4 sm:mr-1.5" />
                      ) : (
                        <Save className="h-4 w-4 sm:mr-1.5" />
                      )}
                      <span className="hidden sm:inline">{getSaveButtonText()}</span>
                    </>
                  )}
                </Button>

                {showTooltip && isSaveButtonDisabled && (
                  <div
                    className="absolute top-full right-0 mt-2 px-3 py-2 bg-popover border border-border text-foreground text-xs rounded-md shadow-lg whitespace-nowrap z-50"
                    role="tooltip"
                  >
                    {t('saveStatus.saved')}
                    <div className="absolute -top-1 right-4 w-2 h-2 bg-popover border-l border-t border-border transform rotate-45"></div>
                  </div>
                )}
              </div>
            )}

            {/* Botão Exportar PDF */}
            {onExportPDF && (
              <Button
                onClick={onExportPDF}
                variant="outline"
                size="sm"
                className="h-8 gap-1.5"
              >
                <FileText className="h-3.5 w-3.5" />
                <span className="hidden sm:inline text-xs">{t('meetingHeader.export')}</span>
              </Button>
            )}

            {/* Menu de opções */}
            {(onClone || onEditProjectCode || (meeting.status !== "final" && onFinalize && canEdit) || (meeting.status === "final" && onReopen && canEdit)) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 gap-1.5">
                    <MoreVertical className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline text-xs">{t('meetingHeader.actions')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {onEditProjectCode && (
                    <DropdownMenuItem onClick={onEditProjectCode}>
                      <Hash className="h-4 w-4 mr-2" />
                      Código da obra
                    </DropdownMenuItem>
                  )}
                  {onClone && (
                    <>
                      {onEditProjectCode && <DropdownMenuSeparator />}
                      <DropdownMenuItem onClick={onClone}>
                        <Copy className="h-4 w-4 mr-2" />
                        {t('meetings.clone')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {meeting.status !== "final" && onFinalize && canEdit && (
                    <>
                      {(onClone || onEditProjectCode) && <DropdownMenuSeparator />}
                      <DropdownMenuItem onClick={onFinalize}>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('meetings.finalize')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {meeting.status === "final" && onReopen && canEdit && (
                    <>
                      {(onClone || onEditProjectCode) && <DropdownMenuSeparator />}
                      <DropdownMenuItem onClick={onReopen}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Reabrir para edição
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <MeetingMetadataFields
          legenda={legenda}
          observacoes={observacoes}
          responsavelId={responsavelId}
          responsibleOptions={responsibleOptions}
          canEdit={canEdit && meeting.status !== "final"}
          onLegendaChange={onLegendaChange}
          onObservacoesChange={onObservacoesChange}
          onResponsavelChange={onResponsavelChange}
        />
      </div>
    </div>
  );
}
